// Ecranul «Export PUD» (27.10.2026): planșa U3 «Reglementări urbanistice» a
// unui plan de situație, scoasă de exporter (v. `exporter/`).
//
// Trei lucruri pe ecran: pornirea exportului și starea rulării lui; fișierele
// (PDF-ul previzualizat în pagină, DXF-ul și faptele de descărcat); și
// registrul faptelor — fiecare valoare cu sursa și starea ei, cu o coloană în
// care omul scrie corecția lui. Corecțiile stau pe proiect
// (`discipline_settings`, cheia `pud`) și se aplică la următoarea generare:
// planșa se desenează o singură dată, pe server, și ce vezi aici e chiar
// fișierul pe care îl descarci.
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Link, useParams } from "react-router-dom";
import * as api from "../api";
import { useRolProiect } from "../components/rol";
import { useT, useTn } from "../i18n";
import { listSolutions, type SolutieRand } from "../nav/api";
import { caleRand, numeRand } from "../nav/selectie";
import { etapaJobului } from "../solutions/shared";
import MeniuExport from "../viewer/tronson/export/MeniuExport";
import "./pud.css";

type Corectii = Record<string, string | number | boolean>;

/** Informațiile care au sens de corectat de mână: valori scalare, nu geometrii. */
const corectabil = (f: api.FaptPud) =>
  f.valoare === null || f.valoare === undefined
  || ["string", "number", "boolean"].includes(typeof f.valoare);

function valoareText(v: unknown): string {
  if (v === null || v === undefined) return "—";
  if (typeof v === "number") return v.toLocaleString("ro-RO", { maximumFractionDigits: 2 });
  if (typeof v === "boolean") return v ? "da" : "nu";
  if (typeof v === "string") return v;
  if (Array.isArray(v)) return `${v.length} elemente`;
  return "…";
}

export default function Pud() {
  const { projectId = "", solutionId = "" } = useParams();
  const t = useT();
  const tn = useTn();
  const { potScrie } = useRolProiect();
  const [sol, setSol] = useState<SolutieRand | null>(null);
  // `null` cât timp lista joburilor n-a venit încă: pornirea automată așteaptă
  // s-o vadă, ca să nu pornească peste un export care există deja
  const [jobs, setJobs] = useState<api.Job[] | null>(null);
  const [fapte, setFapte] = useState<api.FaptePud | null>(null);
  const [corectii, setCorectii] = useState<Corectii>({});
  const [schimbate, setSchimbate] = useState(false);
  const [mesaj, setMesaj] = useState<{ fel: "ok" | "rau"; text: string } | null>(null);
  const [ocupat, setOcupat] = useState(false);
  // versiunea PDF-ului din iframe: se schimbă după fiecare export, ca
  // browserul să nu arate planșa veche din cache
  const [versiune, setVersiune] = useState(0);

  const exporturile = useMemo(
    () => (jobs ?? []).filter((j) => j.kind === "export" && j.upstream_solution_id === solutionId)
      .sort((a, b) => (b.created_at ?? "").localeCompare(a.created_at ?? "")),
    [jobs, solutionId]);
  const ultimul = exporturile[0] ?? null;
  const ruleaza = !!ultimul && (ultimul.status === "pending" || ultimul.status === "running")
    && !ultimul.is_dead;
  const gata = !!ultimul && ultimul.status === "succeeded";

  const citesteJoburile = useCallback(() =>
    api.listJobs(projectId).then(setJobs).catch(() => undefined), [projectId]);
  const citesteFaptele = useCallback(() =>
    api.faptePud(projectId, solutionId).then(setFapte).catch(() => setFapte(null)),
    [projectId, solutionId]);

  useEffect(() => {
    listSolutions(projectId)
      .then((r) => setSol(r.solutii.find((s) => s.id === solutionId) ?? null))
      .catch(() => undefined);
    void citesteJoburile();
    void citesteFaptele();
    api.corectiiPud(projectId).then((r) => setCorectii(r.fapte ?? {})).catch(() => undefined);
  }, [projectId, solutionId, citesteJoburile, citesteFaptele]);

  // cât rulează exportul, starea se recitește la câteva secunde; când s-a
  // terminat, vin și faptele și PDF-ul nou
  useEffect(() => {
    if (!ruleaza) return;
    const ceas = window.setInterval(() => { void citesteJoburile(); }, 3000);
    return () => window.clearInterval(ceas);
  }, [ruleaza, citesteJoburile]);
  useEffect(() => {
    if (gata) { void citesteFaptele(); setVersiune((v) => v + 1); }
  }, [gata, ultimul?.id, citesteFaptele]);

  const porneste = useCallback(async () => {
    setOcupat(true); setMesaj(null);
    try {
      await api.startJob(projectId, "export", { format: "pud", upstream_solution_id: solutionId });
      await citesteJoburile();
    } catch (e) {
      setMesaj({ fel: "rau", text: e instanceof api.ApiError && e.message ? e.message : t("pud.esec_pornire") });
    } finally {
      setOcupat(false);
    }
  }, [projectId, solutionId, citesteJoburile, t]);

  // PORNIREA AUTOMATĂ (Oana, 29.10.2026): cine deschide pagina vrea planșa,
  // nu un buton. Dacă planul n-are niciun export — nici reușit, nici în curs,
  // nici picat —, exportul pornește singur, o singură dată pe vizită; un
  // export picat rămâne la vedere, cu motivul, și se reia doar la cerere.
  const pornitAutomat = useRef(false);
  useEffect(() => {
    if (jobs === null || !potScrie || pornitAutomat.current) return;
    if (exporturile.length > 0) return;
    pornitAutomat.current = true;
    void porneste();
  }, [jobs, exporturile.length, potScrie, porneste]);

  const salveaza = async () => {
    setOcupat(true); setMesaj(null);
    try {
      const r = await api.scrieCorectiiPud(projectId, corectii);
      setCorectii(r.fapte); setSchimbate(false);
      setMesaj({ fel: "ok", text: t("pud.corectii_salvate") });
    } catch {
      setMesaj({ fel: "rau", text: t("pud.esec_corectii") });
    } finally {
      setOcupat(false);
    }
  };

  const corecteaza = (cheie: string, text: string) => {
    setSchimbate(true);
    setCorectii((c) => {
      const n = { ...c };
      if (text.trim() === "") delete n[cheie];
      else {
        const nr = Number(text.replace(",", "."));
        n[cheie] = text.trim() !== "" && !Number.isNaN(nr) && /^[\d.,-]+$/.test(text.trim()) ? nr : text;
      }
      return n;
    });
  };

  // numele actului, în limba ecranului; o sursă neștiută rămâne cum a scris-o serviciul
  function sursa(s: string | null): string {
    switch (s) {
      case null: return "—";
      case "certificat_urbanism": return t("pud.sursa.certificat_urbanism");
      case "carte_funciara": return t("pud.sursa.carte_funciara");
      case "topo": return t("pud.sursa.topo");
      case "puz": return t("pud.sursa.puz");
      case "document": return t("pud.sursa.document");
      case "arhitectura": return t("pud.sursa.arhitectura");
      case "avize": return t("pud.sursa.avize");
      case "manual": return t("pud.sursa.manual");
      case "osm": return t("pud.sursa.osm");
      case "geocodat": return t("pud.sursa.geocodat");
      case "calculat": return t("pud.sursa.calculat");
      default: return s;
    }
  }

  const randuri = useMemo(() => {
    const f = fapte?.fapte ?? [];
    const ordine = { lipsa: 0, aproximat: 1, cunoscut: 2 };
    return [...f].sort((a, b) => ordine[a.stare] - ordine[b.stare]);
  }, [fapte]);
  const lipsa = fapte?.lipsa?.length ?? 0;
  // avertismentele paginii, NU ale planșei (Oana, 31.10.2026): terenul desenat
  // care nu dă suprafața din acte (indicatorii ies pe altă bază decât cea
  // cerută de PUD) și balcoanele neverificate față de edificabil
  const avertismente = useMemo(() => {
    const val = (cheie: string) => {
      const x = fapte?.fapte.find((f) => f.cheie === cheie);
      return x && x.stare !== "lipsa" ? x.valoare : undefined;
    };
    const out: string[] = [];
    const contur = val("suprafata_contur_m2"), acte = val("suprafata_acte_m2");
    if (typeof contur === "number" && typeof acte === "number" && Math.abs(contur - acte) > 1) {
      out.push(t("pud.avert.acte", { contur: valoareText(contur), acte: valoareText(acte) }));
    }
    const balcoane = val("balcoane_m2");
    if (typeof balcoane === "number" && balcoane > 0) {
      out.push(t("pud.avert.balcoane", { m2: valoareText(balcoane) }));
    }
    return out;
  }, [fapte, t]);

  return (
    <div className="ob-pud">
      <div className="ob-pud-cap">
        <h1>{t("pud.titlu")}</h1>
        {sol && (
          <Link to={caleRand(projectId, sol)} className="ob-pud-inapoi">
            {t("pud.inapoi")} · {numeRand(sol)}
          </Link>
        )}
      </div>
      <p className="note ob-pud-explica">{t("pud.explica")}</p>

      <div className="ob-pud-actiuni">
        {potScrie && (
          <button type="button" className="btn btn-primary" disabled={ocupat || ruleaza}
                  onClick={() => { void porneste(); }}>
            {ruleaza ? t("pud.se_genereaza") : gata ? t("pud.regenereaza") : t("pud.genereaza")}
          </button>
        )}
        {ultimul && ruleaza && (
          <span className="ob-pud-stare">{t("pud.rulare", { stare: etapaJobului(ultimul) ?? ultimul.status })}</span>
        )}
        {ultimul && ultimul.status === "failed" && (
          <span className="ob-pud-stare bad">{t("pud.picat", { motiv: ultimul.error?.message ?? ultimul.error?.code ?? "?" })}</span>
        )}
        {gata && (
          <>
            {/* un singur buton de descărcare, formatele sub el; informațiile
                (JSON) nu se descarcă — se citesc în tabelul de alături */}
            <MeniuExport<"pud_pdf" | "pud_dxf">
              titlu={t("pud.descarca")}
              randuri={[
                { format: "pud_pdf", text: t("pud.descarca_pdf"), titlu: t("pud.descarca_pdf_titlu") },
                { format: "pud_dxf", text: t("pud.descarca_dxf"), titlu: t("pud.descarca_dxf_titlu") },
              ]}
              laExport={(fel) => { window.location.assign(api.exportUrl(projectId, solutionId, fel)); }} />
            {fapte && <span className="ob-pud-stare">{t("pud.coala", { coala: fapte.coala, scara: fapte.scara })}
              {lipsa > 0 && <> · <b className="bad">{tn("pud.lipsa#n", lipsa)}</b></>}</span>}
          </>
        )}
        {mesaj && <span className={`ob-pud-stare ${mesaj.fel === "rau" ? "bad" : "ok"}`}>{mesaj.text}</span>}
      </div>
      {avertismente.length > 0 && (
        <ul className="ob-pud-avertismente">
          {avertismente.map((a) => <li key={a} className="note bad">{a}</li>)}
        </ul>
      )}

      <div className="ob-pud-corp">
        <section className="ob-pud-previz">
          <h2>{t("pud.previzualizare")}</h2>
          {gata ? (
            <iframe key={versiune} title="PUD U3"
                    src={`${api.exportUrl(projectId, solutionId, "pud_pdf", true)}&v=${versiune}`} />
          ) : (
            <p className="note">{t("pud.fara_export")}</p>
          )}
        </section>

        <section className="ob-pud-fapte">
          <h2>{t("pud.fapte")}</h2>
          <p className="note">{t("pud.fapte_explica")}</p>
          {randuri.length > 0 && (
            <table>
              <thead>
                <tr>
                  <th>{t("pud.col_fapt")}</th>
                  <th>{t("pud.col_valoare")}</th>
                  <th>{t("pud.col_sursa")}</th>
                  {potScrie && <th>{t("pud.col_corectie")}</th>}
                </tr>
              </thead>
              <tbody>
                {randuri.map((f) => (
                  <tr key={f.cheie} className={`stare-${f.stare}`} title={f.nota ?? undefined}>
                    <td className="cheie">{f.cheie.replace(/_/g, " ")}</td>
                    <td className="valoare">{valoareText(f.valoare)}{f.um ? ` ${f.um}` : ""}</td>
                    <td className="sursa">
                      <span className={`pastila ${f.stare}`}>{t(`pud.stare.${f.stare}` as const)}</span>
                      {" "}{sursa(f.sursa)}
                    </td>
                    {potScrie && (
                      <td className="corectie">
                        {corectabil(f) && (
                          <input type="text" value={corectii[f.cheie] === undefined ? "" : String(corectii[f.cheie])}
                                 placeholder={f.stare === "lipsa" ? "…" : ""}
                                 onChange={(e) => corecteaza(f.cheie, e.target.value)} />
                        )}
                      </td>
                    )}
                  </tr>
                ))}
              </tbody>
            </table>
          )}
          {potScrie && schimbate && (
            <button type="button" className="btn" disabled={ocupat} onClick={() => { void salveaza(); }}>
              {t("pud.salveaza_corectii")}
            </button>
          )}
        </section>
      </div>
    </div>
  );
}
